import React, { useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { Button, Dialog, Group } from '@economic/taco';
import { FormApi } from 'final-form';

import { useNavigate } from 'react-router-dom';
import { Field, Form } from '~/app/shared/components/Form';
import { Column, Row } from '~/app/shared/components/Grid/Grid';
import ProductCombobox from '~/app/shared/components/ui/ProductCombobox';
import internalApiClient from '~/app/shared/util/api/internalApiClient';
import track from '~/app/shared/util/utilities/mixpanel';
import { CreatePartialInvoiceRequest, PartialInvoiceDistributionType } from '~/types/internal-api';
import PartialInvoicingBalanceInfo from '../PartialInvoicingBalanceInfo';
import { PartialInvoiceUpdater } from './CreatePartialInvoiceForm.updater';
import { validationSchema } from './CreatePartialInvoice.validator';
import ErrorMessageAlert from '../ProductLineBasedPartialInvoicing/ErrorMessageAlert';

const initialData = {
    productId: null,
    isFixedAmount: true,
    amount: null,
};

interface Props {
    close: () => void;
    orderId: number;
    orderAmount: number;
    orderRemainingAmount: number;
    handleShowConvertToInvoiceConfirmation: () => void;
    hasDraftPartialInvoices: boolean;
    partialInvoicesCount: number;
}

export default function CreatePartialInvoiceForm({
    close,
    orderId,
    orderAmount,
    orderRemainingAmount,
    handleShowConvertToInvoiceConfirmation,
    hasDraftPartialInvoices,
    partialInvoicesCount,
}: Props) {
    const navigate = useNavigate();
    const [prevOrderRemainingAmount, setPrevOrderRemainingAmount] = useState<number>(orderRemainingAmount);
    const [formKey, setFormKey] = useState<number>(1);

    // formKey and order remaining amount are stored in local state in order to reset the form and its validation schema properly.
    // We need to reset the form after partial invoices are done refetching in order to update validation schema with new information about draft partial invoices
    if (orderRemainingAmount !== prevOrderRemainingAmount) {
        setPrevOrderRemainingAmount(orderRemainingAmount);
        setFormKey(formKey + 1);
    }

    const isTryingToInvoiceFullRemainingAmount = values => {
        return (
            (values.isFixedAmount && values.amount === orderRemainingAmount) ||
            (!values.isFixedAmount && (orderAmount * values.amount) / 100.0 === orderRemainingAmount)
        );
    };

    const onCreatePartialInvoiceClick = (values, form: FormApi) => {
        if (!hasDraftPartialInvoices && isTryingToInvoiceFullRemainingAmount(values)) {
            handleShowConvertToInvoiceConfirmation();
        } else {
            form.submit();
        }
    };

    const submit = async values => {
        const request: CreatePartialInvoiceRequest = {
            salesDocumentId: orderId,
            productId: values.productId,
            isFixedAmount: values.isFixedAmount,
            amount: values.amount,
        };

        await internalApiClient
            .post(`/sales/partial-invoice`, request)
            .then(response => {
                const partialInvoiceSalesDocumentId = response.data;
                track('partial-invoicing', 'created', {
                    distributionType: PartialInvoiceDistributionType.AmountBased,
                    invoiceCount: partialInvoicesCount + 1,
                });
                navigate(`sales/invoicing/invoices/${partialInvoiceSalesDocumentId}`);
            })
            .catch(error => {
                return Promise.reject(error);
            });
    };

    const orderRemainingAmountInclFormAmount = (formAmount: number, isFixedAmount: boolean) => {
        if (!formAmount) {
            return orderRemainingAmount;
        }

        if (isFixedAmount) {
            return orderRemainingAmount - formAmount;
        }
        return orderRemainingAmount - (orderAmount * formAmount) / 100.0;
    };

    return (
        <Form
            messages={{
                success: t`Success`,
            }}
            key={formKey}
            onSubmit={submit}
            initialData={initialData}
            validationSchema={() => validationSchema(orderAmount, orderRemainingAmount, hasDraftPartialInvoices)}
            updates={PartialInvoiceUpdater}>
            {({ values, form, errors }) => (
                <>
                    <Row divided>
                        <Column xs="6">
                            <Field.Combobox
                                fieldClassName="mt-2"
                                required
                                showCreateOnTheFly
                                label={t`Select the product no. to base partial invoice on`}
                                name="productId"
                                as={ProductCombobox}
                            />
                        </Column>
                        <Column xs="6">
                            <Field.Dropdown
                                required
                                fieldClassName="mt-2"
                                label={t`Select calculation method for partial invoicing`}
                                name="isFixedAmount"
                                options={[
                                    { text: t`Fixed amount`, value: true },
                                    { text: t`Percentage`, value: false },
                                ]}
                            />
                            <Field.Number
                                required
                                label={values.isFixedAmount ? t`Amount` : t`Percentage of total order amount`}
                                name="amount"
                                decimal
                                postfix={
                                    <Button
                                        appearance="discrete"
                                        onClick={() => {
                                            let remainingAmount;
                                            if (values.isFixedAmount) {
                                                remainingAmount = orderRemainingAmount;
                                            } else {
                                                remainingAmount = (orderRemainingAmount * 100.0) / orderAmount;
                                            }
                                            form.change('amount', remainingAmount);
                                            form.blur('amount');
                                        }}>
                                        <Trans>use remaining</Trans>
                                    </Button>
                                }
                            />
                        </Column>
                    </Row>
                    <PartialInvoicingBalanceInfo
                        textGroups={[
                            {
                                label: t`Remaining amount incl. new partial invoice`,
                                value: orderRemainingAmountInclFormAmount(values.amount, values.isFixedAmount),
                            },
                        ]}
                    />
                    <ErrorMessageAlert
                        errors={errors}
                        shouldShowConvertToInvoiceButton={isTryingToInvoiceFullRemainingAmount(values)}
                        hasDraftPartialInvoices={hasDraftPartialInvoices}
                    />
                    <Dialog.Footer>
                        <Group>
                            <Button onClick={close}>
                                <Trans>Cancel</Trans>
                            </Button>
                            <Button appearance="primary" onClick={() => onCreatePartialInvoiceClick(values, form)}>
                                <Trans>Create partial invoice</Trans>
                            </Button>
                        </Group>
                    </Dialog.Footer>
                </>
            )}
        </Form>
    );
}
